<script setup lang="ts">
import {
  UserAvatar,
  userInjectionKey,
  useTimeSinceString,
  type User,
  guest,
  useClickOutside,
  globalOverlayContainer,
  FloatingBox,
  navigateInternally,
  type BaseUser,
} from '#shared'
import { useQuery } from '@tanstack/vue-query'
import { computed, inject, ref, unref, useTemplateRef, watch } from 'vue'
import { getFullscreenChatHref } from '#entities/chat'
import { getPersonalChatQueryOptions } from '../state/personal-chat-query'
import { useCreateChatMutation } from '../state/use-create-chat-mutation'

const {
  isOpen,
  user,
  targetEl,
  verticalOffset = 0,
} = defineProps<{ isOpen: boolean; user: User | BaseUser; targetEl: HTMLElement | null; verticalOffset?: number }>()

const emit = defineEmits<{ close: [] }>()

const currentUser = inject(userInjectionKey)

const formattedJoinedAt = computed(() => {
  if (!('joinedAt' in user)) return ''

  return new Date(user.joinedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
})

const formattedLastSeen = useTimeSinceString(new Date())

const isPersonalChatQueryEnabled = computed(() => {
  if (unref(currentUser) === guest) return false
  if (!isOpen) return false
  return true
})
const personalChatQuery = useQuery({ ...getPersonalChatQueryOptions(user.id), enabled: isPersonalChatQueryEnabled })
const personalChat = personalChatQuery.data

const navigateToChat = (chatId: number) => {
  const targetHref = getFullscreenChatHref(chatId)

  if (navigateInternally(targetHref)) return
  location.assign(targetHref)
}

const createChatMutation = useCreateChatMutation({
  onSuccess: response => {
    navigateToChat(response.id)
  },
})

const errorMessage = computed(() => {
  if (personalChatQuery.isError.value && unref(currentUser) !== guest) return 'Failed to load chat information'
  if (createChatMutation.isError.value) return 'Failed to open the chat'
})

const canStartConversation = computed(() => {
  const currentUserVal = unref(currentUser)
  if (!currentUserVal) return false
  if (currentUserVal === guest) return false
  if (currentUserVal.id === user.id) return false
  return true
})
const onConversationStart = () => {
  const personalChatVal = personalChat.value
  if (personalChatVal) {
    navigateToChat(personalChatVal.id)
  } else {
    createChatMutation.mutate(user.id)
  }
}

const isClosable = ref(isOpen)
watch(
  () => isOpen,
  isOpen => setTimeout(() => (isClosable.value = isOpen))
)

const rootEl = useTemplateRef('rootEl')
useClickOutside(
  computed(() => [rootEl.value]),
  () => {
    if (!isClosable.value) return
    emit('close')
  }
)
</script>

<template>
  <Teleport :to="globalOverlayContainer">
    <Transition
      :enterFromClass="$style.appearEnterFrom"
      :enterActiveClass="$style.appearEnterActive"
      :leaveToClass="$style.appearLeaveTo"
      :leaveActiveClass="$style.appearLeaveActive"
    >
      <FloatingBox
        v-if="isOpen"
        :targetEl="targetEl"
        verticalPosition="outside"
        horizontalPosition="left-inside"
        :verticalOffset="verticalOffset"
      >
        <div ref="rootEl" :class="$style.root" data-behavior="chat/user-profile-preview">
          <div :class="$style.info">
            <div :class="$style.avatarWrap">
              <UserAvatar :user="user" size="large" />
            </div>

            <div :class="$style.textWrap">
              <div :class="$style.name" :title="user.displayName">
                {{ user.displayName }}
              </div>

              <div v-if="'login' in user" :class="$style.login">@{{ user.login }}</div>

              <div :class="$style.infoPointsWrap">
                <div :class="$style.infoPoint">Joined {{ formattedJoinedAt }}</div>
                <div :class="$style.infoPoint">
                  {{ formattedLastSeen }}
                </div>
                <div v-if="'totalMessagesCount' in user" :class="$style.infoPoint">
                  Messages: {{ user.totalMessagesCount }}
                </div>
              </div>
            </div>
          </div>

          <div :class="$style.actions">
            <button
              v-if="canStartConversation"
              :class="$style.startConversation"
              :disabled="createChatMutation.isPending.value"
              @click="onConversationStart"
            >
              {{ personalChatQuery.isPending.value ? 'Loading...' : 'Start conversation' }}
            </button>
          </div>

          <div v-if="errorMessage" :class="$style.error">
            {{ errorMessage }}
          </div>
        </div>
      </FloatingBox>
    </Transition>
  </Teleport>
</template>

<style lang="scss" module>
@use '#mixins/button' as button;
@use '#mixins/media' as media;

.root {
  width: 400px;
  max-width: calc(100vw - 40px);
  padding: 20px;
  border-radius: 4px;
  background-color: #26262d;
  z-index: var(--sc-z-index-floating-box);
  border: 1px solid #404045;

  @include media.max-tablet {
    bottom: calc(var(--sc-height-tabs) + 20px);
  }

  @include media.min-desktop {
    bottom: 20px;
  }
}

.info {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.avatarWrap {
  flex-shrink: 0;
}

.textWrap {
  padding-top: 20px;
}

.name {
  font-size: 24px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.login {
  font-size: 16px;
  color: white;
  margin-bottom: 10px;
}

.infoPointsWrap {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.infoPoint {
  font-size: 15px;
  color: #8f8f90;
}

.infoPointImportant {
  color: white;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.startConversation {
  @include button.primary;
}

.error {
  color: var(--sc-error);
  text-align: right;
  margin-top: 10px;
}

.appearEnterFrom,
.appearLeaveTo {
  opacity: 0;
  transform: translateY(10px);
}

.appearEnterActive,
.appearLeaveActive {
  transition:
    opacity 0.2s ease-in-out,
    transform 0.2s ease-in-out;
}
</style>
